import React, { useMemo } from "react";
import Button from "react-bootstrap/Button";
import Table from "react-bootstrap/Table";
import moment from "moment";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// Helpers
const safeSheetName = (name) => {
  // Excel limits: 31 chars, cannot contain: \ / ? * [ ]
  const cleaned = String(name || "Sheet")
    .replace(/[\\\/\?\*\[\]]/g, " ")
    .trim()
    .slice(0, 31);
  return cleaned.length ? cleaned : "Sheet";
};

const escapeForCSV = (value) => {
  const s = value === null || value === undefined ? "" : String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const formatMoneyNumber = (val) => {
  if (val === null || val === undefined) return 0;
  if (typeof val === "number") return val;

  const n = Number(String(val).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const getUser = (users, userID) => {
  if (!users) return null;
  return users[userID] || null;
};

const buildSortedReports = (reports, users) => {
  const pairs = [];
  for (const i in reports) {
    if (Object.prototype.hasOwnProperty.call(reports, i)) {
      pairs.push([i, reports[i]]);
    }
  }

  pairs.sort((a, b) => {
    const ra = a[1];
    const rb = b[1];

    const ua = getUser(users, ra.userID);
    const ub = getUser(users, rb.userID);

    const lastA = (ua && ua.lastName) ? ua.lastName : "";
    const lastB = (ub && ub.lastName) ? ub.lastName : "";

    if (lastA === lastB) {
      const da = moment(ra.projectDate);
      const db = moment(rb.projectDate);
      if (da.isAfter(db)) return 1;
      if (da.isBefore(db)) return -1;
      return 0;
    } else {
      if (lastA > lastB) return 2;
      if (lastA < lastB) return -2;
      return 0;
    }
  });

  return pairs;
};

const buildCSV = (sortedReports, users) => {
  const header = ["Name", "Date", "Description", "Project_Site", "Amount", "URL"];
  const lines = [header.map(escapeForCSV).join(",")];

  for (let i = 0; i < sortedReports.length; i++) {
    const report = sortedReports[i][1];
    const u = getUser(users, report.userID);

    const first = (u && u.firstName) ? u.firstName : "";
    const last = (u && u.lastName) ? u.lastName : "";
    const name = (first + " " + last).trim();

    const row = [
      name,
      moment(report.projectDate).format("MM/DD/YY"),
      report.type || "",
      report.client || "",
      report.totalCost !== undefined && report.totalCost !== null ? report.totalCost : "",
      report.pictureURL || "",
    ];

    lines.push(row.map(escapeForCSV).join(","));
  }

  return lines.join("\n") + "\n";
};

const ExcelExporter = (props) => {
  const reports = props.reports;
  const startDate = props.startDate;
  const endDate = props.endDate;
  const uniqueUsers = props.uniqueUsers;
  const users = props.users;

  const sortedReports = useMemo(() => buildSortedReports(reports, users), [reports, users]);
  const csvString = useMemo(() => buildCSV(sortedReports, users), [sortedReports, users]);

  const copyCSV = async () => {
    try {
      await navigator.clipboard.writeText(csvString);
    } catch (e) {
      console.error("Clipboard error:", e);
      // Fallback for older browsers
      try {
        const ta = document.createElement("textarea");
        ta.value = csvString;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (err) {
        console.error("Clipboard fallback failed:", err);
      }
    }
  };

  const exportExcel = () => {
    try {
      const wb = XLSX.utils.book_new();

      // Summary first row
      const summaryRows = [["Name", "Report Count", "Total Amount"]];
      const perUserTotals = [];

      // Create a sheet per user
      for (let uIdx = 0; uIdx < uniqueUsers.length; uIdx++) {
        const userID = uniqueUsers[uIdx];
        const u = getUser(users, userID);

        const first = (u && u.firstName) ? u.firstName : "";
        const last = (u && u.lastName) ? u.lastName : "";
        const fullName = (first + " " + last).trim() || String(userID);

        const rows = [];
        rows.push(["Date", "Description", "Project_Site", "Amount", "URL"]);

        let total = 0;
        let count = 0;

        for (let rIdx = 0; rIdx < sortedReports.length; rIdx++) {
          const report = sortedReports[rIdx][1];
          if (report.userID !== userID) continue;

          const amountNum = formatMoneyNumber(report.totalCost);
          total += amountNum;
          count += 1;

          rows.push([
            moment(report.projectDate).format("MM/DD/YY"),
            report.type || "",
            report.client || "",
            amountNum,
            report.pictureURL || "",
          ]);
        }

        // Footer (TOTAL)
        rows.push([]);
        rows.push([fullName, "", "TOTAL", total, ""]);

        const ws = XLSX.utils.aoa_to_sheet(rows);

        // Column widths
        ws["!cols"] = [
          { wch: 12 },
          { wch: 22 },
          { wch: 30 },
          { wch: 12 },
          { wch: 40 },
        ];

        // Currency format on column D
        for (let rr = 2; rr <= rows.length; rr++) {
          const cellAddr = XLSX.utils.encode_cell({ r: rr - 1, c: 3 });
          const cell = ws[cellAddr];
          if (cell && typeof cell.v === "number") {
            cell.z = "$#,##0.00";
          }
        }

        XLSX.utils.book_append_sheet(wb, ws, safeSheetName(fullName));

        perUserTotals.push({ name: fullName, count: count, total: total });
      }

      // Summary sheet
      for (let i = 0; i < perUserTotals.length; i++) {
        summaryRows.push([perUserTotals[i].name, perUserTotals[i].count, perUserTotals[i].total]);
      }

      const wsSummary = XLSX.utils.aoa_to_sheet(summaryRows);
      wsSummary["!cols"] = [{ wch: 28 }, { wch: 14 }, { wch: 14 }];

      for (let rr = 2; rr <= summaryRows.length; rr++) {
        const addr = XLSX.utils.encode_cell({ r: rr - 1, c: 2 });
        const cell = wsSummary[addr];
        if (cell && typeof cell.v === "number") cell.z = "$#,##0.00";
      }

      XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");

      const title = (`Expense_Report_${startDate}_to_${endDate}`).replace(/[^\w\-]+/g, "_");
      const fileData = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(new Blob([fileData], { type: "application/octet-stream" }), `${title}.xlsx`);
    } catch (e) {
      console.error("Excel export error:", e);
    }
  };

  return (
    <div>
      <div>
        <Button onClick={exportExcel}>Export Excel</Button>
        <Button variant="warning" style={{ marginLeft: 30 }} onClick={copyCSV}>
          CSV to Clipboard
        </Button>
      </div>

      <div>
        <Table striped bordered hover style={{ marginTop: 20 }}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Date</th>
              <th>Description</th>
              <th>Project_Site</th>
              <th>Amount</th>
              <th>URL</th>
            </tr>
          </thead>
          <tbody>
            {sortedReports.map((entry) => {
              const report = entry[1];
              const u = getUser(users, report.userID);

              const first = (u && u.firstName) ? u.firstName : "";
              const last = (u && u.lastName) ? u.lastName : "";
              const name = (first + " " + last).trim();

              return (
                <tr key={report.key || `${report.userID}-${report.projectDate}-${report.totalCost}`}>
                  <td>{name}</td>
                  <td>{moment(report.projectDate).format("MM/DD/YY")}</td>
                  <td>{report.type}</td>
                  <td>{report.client}</td>
                  <td>{report.totalCost}</td>
                  <td>{report.pictureURL}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default ExcelExporter;
